import { createFileRoute, Link, Outlet, useLocation, useNavigate } from "@tanstack/react-router";
import { fetchAllRows, fetchByIds, fetchOrgMembers } from "@/lib/large-org";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { ShieldCheck, Building2, Users, MapPin, Settings2, Crown, Mail } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { platformInviteUser } from "@/lib/platform-invites.functions";
import { useAccessibleTabs } from "@/components/ui/a11y-tabs";

export const Route = createFileRoute("/_authenticated/admin")({
  component: AdminPanel,
});

function PlatformInviteCard() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const invite = useServerFn(platformInviteUser);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      const redirectTo = typeof window !== "undefined" ? `${window.location.origin}/onboarding` : undefined;
      await invite({ data: { email: email.trim().toLowerCase(), redirectTo } });
      toast.success(`Invite email sent to ${email.trim().toLowerCase()}`);
      setEmail("");
    } catch (err: any) {
      toast.error(err?.message ?? "Failed to send invite");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card className="mb-4 p-4">
      <div className="mb-3 flex items-center gap-2">
        <Mail className="h-4 w-4 text-primary" />
        <h3 className="font-semibold">Invite a new user to the platform</h3>
      </div>
      <p className="mb-3 text-xs text-muted-foreground">
        Sends an official sign-up email. The recipient lands on onboarding and creates their own free-trial workspace.
      </p>
      <form onSubmit={submit} className="flex flex-col gap-2 sm:flex-row sm:items-end">
        <div className="flex-1">
          <Label htmlFor="platform-invite-email" className="text-xs">Email address</Label>
          <Input
            id="platform-invite-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="person@example.com"
            className="mt-1"
            autoComplete="email"
          />
        </div>
        <Button type="submit" disabled={busy}>{busy ? "Sending…" : "Send invite"}</Button>
      </form>
    </Card>
  );
}

type Org = { id: string; name: string; slug: string; plan: string; created_at: string };
type Member = { user_id: string; role: string; organization_id: string };
type Profile = { id: string; email: string | null; full_name: string | null };
type Site = { id: string; name: string; organization_id: string };
type PA = { user_id: string; email: string; granted_at: string };

function AdminPanel() {
  const { isPlatformAdmin, user, setCurrentOrgId } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [profiles, setProfiles] = useState<Record<string, Profile>>({});
  const [sites, setSites] = useState<Site[]>([]);
  const [admins, setAdmins] = useState<PA[]>([]);
  const [tab, setTab] = useState<"orgs" | "users" | "sites" | "admins">("orgs");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isPlatformAdmin) return;
    (async () => {
      setLoading(true);
      const [o, m, s, a] = await Promise.all([
        supabase.from("organizations").select("id, name, slug, plan, created_at").order("created_at", { ascending: false }),
        fetchAllRows((f, t) => supabase.from("organization_members").select("user_id, role, organization_id").order("user_id").range(f, t)).then((data) => ({ data })),
        supabase.from("sites").select("id, name, organization_id").limit(500),
        supabase.from("platform_admins").select("user_id, email, granted_at"),
      ]);
      const memberList = (m.data ?? []) as Member[];
      setOrgs((o.data ?? []) as Org[]);
      setMembers(memberList);
      setSites((s.data ?? []) as Site[]);
      setAdmins((a.data ?? []) as PA[]);
      const userIds = Array.from(new Set(memberList.map((mm) => mm.user_id)));
      if (userIds.length) {
        const pdata = await fetchByIds<Profile>("profiles", "id, email, full_name", userIds);
        const map: Record<string, Profile> = {};
        (pdata ?? []).forEach((p: Profile) => { map[p.id] = p; });
        setProfiles(map);
      }
      setLoading(false);
    })();
  }, [isPlatformAdmin]);

  if (!isPlatformAdmin) {
    return (
      <div className="px-4 py-10 md:px-8">
        <Card className="mx-auto max-w-lg p-8 text-center">
          <ShieldCheck className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
          <h1 className="text-lg font-semibold">Restricted</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            The Platform Admin panel is reserved for the platform owner.
          </p>
        </Card>
      </div>
    );
  }

  const updateRole = async (uid: string, orgId: string, role: string) => {
    const { error } = await supabase.rpc("platform_admin_set_member_role", {
      _user_id: uid,
      _organization_id: orgId,
      _role: role as never,
    });
    if (error) { console.error(error); return; }
    setMembers((prev) => prev.map((mm) => (mm.user_id === uid && mm.organization_id === orgId ? { ...mm, role } : mm)));
  };

  const a11yTabs = useAccessibleTabs(["orgs", "users", "sites", "admins"] as const, tab, setTab);
  const tabs = [
    { id: "orgs" as const, label: "Organizations", icon: Building2, count: orgs.length },
    { id: "users" as const, label: "Users", icon: Users, count: members.length },
    { id: "sites" as const, label: "Sites", icon: MapPin, count: sites.length },
    { id: "admins" as const, label: "Platform Admins", icon: Crown, count: admins.length },
  ];

  if (location.pathname !== "/admin") return <Outlet />;

  return (
    <div className="px-4 py-6 md:px-8 md:py-8">
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-primary">Platform Admin</p>
          <h1 className="font-display text-2xl font-bold md:text-3xl">Mission Control</h1>
          <p className="text-sm text-muted-foreground">
            Signed in as <span className="font-medium">{user?.email}</span> — full access across all organizations.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/admin/contact"
            className="rounded-md border border-border bg-card px-3 py-2 text-xs font-semibold text-foreground hover:border-primary hover:text-primary"
          >
            Contact messages
          </Link>
          <Link
            to="/admin/signups"
            className="rounded-md border border-border bg-card px-3 py-2 text-xs font-semibold text-foreground hover:border-primary hover:text-primary"
          >
            Signups
          </Link>
          <Link
            to="/admin/funnel"
            className="rounded-md border border-border bg-card px-3 py-2 text-xs font-semibold text-foreground hover:border-primary hover:text-primary"
          >
            Funnel
          </Link>
          <Link
            to="/admin/traffic"
            className="rounded-md border border-border bg-card px-3 py-2 text-xs font-semibold text-foreground hover:border-primary hover:text-primary"
          >
            Traffic quality
          </Link>

          <Link
            to="/admin/web-vitals"
            className="rounded-md border border-border bg-card px-3 py-2 text-xs font-semibold text-foreground hover:border-primary hover:text-primary"
          >
            Web Vitals
          </Link>
          <Link
            to="/admin/audit"
            className="rounded-md border border-border bg-card px-3 py-2 text-xs font-semibold text-foreground hover:border-primary hover:text-primary"
          >
            Audit log
          </Link>
          <Link
            to="/security"
            className="rounded-md border border-border bg-card px-3 py-2 text-xs font-semibold text-foreground hover:border-primary hover:text-primary"
          >
            Security center
          </Link>
          <div className="hidden rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-xs font-semibold text-primary md:flex md:items-center md:gap-2">
            <Crown className="h-4 w-4" /> Platform Owner
          </div>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap gap-2 border-b border-border" aria-label="Platform sections" {...a11yTabs.tablistProps}>
        {tabs.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              {...a11yTabs.tabProps(t.id)}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 rounded-t-md border-b-2 px-4 py-2 text-sm transition-colors ${
                active ? "border-primary text-primary font-semibold" : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <Icon className="h-4 w-4" /> {t.label}
              <span className="ml-1 rounded-full bg-muted px-2 py-0.5 text-xs">{t.count}</span>
            </button>
          );
        })}
      </div>

      {loading && <div className="text-sm text-muted-foreground" role="status">Loading…</div>}

      {!loading && tab === "orgs" && (
        <div className="grid gap-3 md:grid-cols-2" {...a11yTabs.panelProps("orgs")}>
          {orgs.map((o) => {
            const memberCount = members.filter((m) => m.organization_id === o.id).length;
            const siteCount = sites.filter((s) => s.organization_id === o.id).length;
            return (
              <Card
                key={o.id}
                onClick={() => { setCurrentOrgId(o.id); navigate({ to: "/operations" }); }}
                className="cursor-pointer p-4 transition-colors hover:border-primary/60"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-primary" />
                      <h3 className="truncate font-semibold">{o.name}</h3>
                    </div>
                    <p className="mt-1 truncate text-xs text-muted-foreground">@{o.slug}</p>
                  </div>
                  <span className="rounded-full bg-muted px-2 py-1 text-xs font-medium uppercase">{o.plan}</span>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                  <div><span className="font-semibold text-foreground">{memberCount}</span> members</div>
                  <div><span className="font-semibold text-foreground">{siteCount}</span> sites</div>
                </div>
                <div className="mt-2 text-[11px] font-semibold text-primary">Open workspace →</div>
              </Card>
            );
          })}
        </div>
      )}

      {!loading && tab === "users" && (
        <Card className="overflow-hidden" {...a11yTabs.panelProps("users")}>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">User</th>
                  <th className="px-3 py-2 text-left">Organization</th>
                  <th className="px-3 py-2 text-left">Role</th>
                </tr>
              </thead>
              <tbody>
                {members.map((m) => {
                  const org = orgs.find((o) => o.id === m.organization_id);
                  return (
                    <tr key={`${m.user_id}-${m.organization_id}`} className="border-t border-border">
                      <td className="px-3 py-2">
                        <div className="font-medium">{profiles[m.user_id]?.full_name ?? "—"}</div>
                        <div className="text-xs text-muted-foreground">{profiles[m.user_id]?.email ?? m.user_id.slice(0, 8)}</div>
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">{org?.name ?? m.organization_id.slice(0, 8)}</td>
                      <td className="px-3 py-2">
                        <select
                          value={m.role}
                          onChange={(e) => updateRole(m.user_id, m.organization_id, e.target.value)}
                          className="rounded-md border border-border bg-background px-2 py-1 text-xs"
                        >
                          {["org_owner", "admin", "supervisor", "site_supervisor", "dispatcher", "officer", "auditor", "client_viewer"].map((r) => (
                            <option key={r} value={r}>{r}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {!loading && tab === "sites" && (
        <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3" {...a11yTabs.panelProps("sites")}>
          {sites.map((s) => {
            const org = orgs.find((o) => o.id === s.organization_id);
            return (
              <Card
                key={s.id}
                onClick={() => {
                  setCurrentOrgId(s.organization_id);
                  navigate({ to: "/sites/$siteId/edit", params: { siteId: s.id } });
                }}
                className="flex cursor-pointer items-center gap-3 p-3 transition-colors hover:border-primary/60"
              >
                <MapPin className="h-4 w-4 text-primary" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{s.name}</div>
                  <div className="truncate text-xs text-muted-foreground">{org?.name ?? "—"}</div>
                </div>
                <span className="text-[11px] font-semibold text-primary">Edit →</span>
              </Card>
            );
          })}
        </div>
      )}

      {!loading && tab === "admins" && (
        <div {...a11yTabs.panelProps("admins")}>
          <PlatformInviteCard />
          <Card className="p-4">
          <div className="mb-3 flex items-center gap-2">
            <Crown className="h-4 w-4 text-primary" />
            <h3 className="font-semibold">Platform owners</h3>
          </div>
          <p className="mb-4 text-xs text-muted-foreground">
            These accounts sit above all organization owners and have full read/write across the platform.
          </p>
          <ul className="divide-y divide-border">
            {admins.map((a) => (
              <li key={a.user_id} className="flex items-center justify-between py-2 text-sm">
                <div>
                  <div className="font-medium">{a.email}</div>
                  <div className="text-xs text-muted-foreground">Granted {new Date(a.granted_at).toLocaleDateString()}</div>
                </div>
                <Link to="/settings" className="text-xs text-primary hover:underline">
                  <Settings2 className="inline h-3 w-3" /> manage
                </Link>
              </li>
            ))}
          </ul>
        </Card>
        </div>
      )}
    </div>
  );
}
